import { useEffect, useState } from 'react';
import { C } from '../utils/constants';

/**
 * One-time "check your email" reminder for a buyer arriving on /login straight
 * from the sales page's success screen.
 *
 * WHO SEES IT. The sales page sends a new buyer to /login?from=purchase — the
 * only thing it carries (no address, name or id). Nobody else arrives with that
 * parameter, so an existing client logging in on a new device never sees it.
 *
 * ONCE. The parameter is removed from the address bar as soon as it is read, so
 * a refresh does not bring the reminder back; pressing OK also records it on
 * this browser, so a later arrival with the parameter shows nothing either.
 *
 * NEVER IN THE WAY. A single OK button, and nothing here touches the login form.
 *
 * THE FIRST NAME. The sales page hands the buyer's first name over in one
 * short-lived cookie, ry_welcome_name (Domain=.benmilston.com, Path=/login,
 * Max-Age=120, Secure, SameSite=Strict), written just before the redirect —
 * never in the address. It is read ONCE here and deleted on the first render,
 * whether or not the popup shows. It is rendered as React text (never HTML),
 * and masked from Microsoft Clarity's session recording (data-clarity-mask),
 * which runs on this page. No name: the greeting reads "Hey, welcome!".
 *
 * Overlay + card pattern matches CallDayNudgeModal.jsx / SupportModal.jsx.
 */
const SEEN_KEY = 'ry_purchase_reminder_seen';
const NAME_COOKIE = 'ry_welcome_name';

function readWelcomeName() {
  try {
    const hit = document.cookie.split('; ').find(c => c.startsWith(NAME_COOKIE + '='));
    if (!hit) return null;
    const name = decodeURIComponent(hit.slice(NAME_COOKIE.length + 1)).trim().slice(0, 60);
    return name || null;
  } catch {
    return null;
  }
}

// Deleted with the same Domain and Path it was written with, and host-only too
// in case it was written without a Domain.
function forgetWelcomeName() {
  try {
    const gone = `${NAME_COOKIE}=; Path=/login; Max-Age=0; Secure; SameSite=Strict`;
    document.cookie = `${gone}; Domain=.benmilston.com`;
    document.cookie = gone;
  } catch { /* nothing to clear */ }
}

function arrivedFromPurchase() {
  try {
    if (new URLSearchParams(window.location.search).get('from') !== 'purchase') return false;
    return localStorage.getItem(SEEN_KEY) !== '1';
  } catch {
    return false;
  }
}

function forgetArrivalParam() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('from')) return;
    params.delete('from');
    const qs = params.toString();
    window.history.replaceState(window.history.state, '',
      window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
  } catch { /* the reminder still works; a refresh would just show it again */ }
}

export const PurchaseReminderModal = () => {
  const [open, setOpen] = useState(arrivedFromPurchase);
  const [firstName] = useState(readWelcomeName);
  useEffect(() => { forgetArrivalParam(); forgetWelcomeName(); }, []);
  if (!open) return null;

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* private mode: still closes */ }
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="purchase-reminder-text"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 16, padding: '28px 28px 24px',
        width: '100%', maxWidth: 380, textAlign: 'center',
        boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
        animation: 'fadeInUp 0.3s ease both',
      }}>
        <p id="purchase-reminder-text" style={{
          color: C.brown, fontSize: '1.05rem', lineHeight: 1.6,
          marginBottom: 22, fontFamily: "'Nunito', sans-serif", fontWeight: 600,
        }}>
          <span style={{ display: 'block' }}>
            {firstName
              ? <>Hey <span data-clarity-mask="true">{firstName}</span>, welcome!</>
              : 'Hey, welcome!'}
          </span>
          <span style={{ display: 'block' }}>Check your email (and your spam folder) for your login details.</span>
          <span style={{ display: 'block' }}>Once you're in, you'll be fully onboarded.</span>
          <span style={{ display: 'block' }}>See you inside!</span>
        </p>
        <button className="btn-primary" onClick={dismiss} autoFocus style={{ minWidth: 140 }}>
          OK
        </button>
      </div>
    </div>
  );
};
